import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import SurfAreaEditModal from "@/components/SurfAreaEditModal";

export default function Admin() {
  const [tab, setTab] = useState("dashboard");
  const [d, setD] = useState(null);
  const [areas, setAreas] = useState([]);
  const [reports, setReports] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [leads, setLeads] = useState([]);
  const [health, setHealth] = useState([]);
  const [audit, setAudit] = useState([]);
  const [editArea, setEditArea] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const reloadAreas = () => api.get("/admin/surf-areas").then((r) => setAreas(r.data.items)).catch(() => {});

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "areas") reloadAreas();
    if (tab === "reports") api.get("/admin/reports").then((r) => setReports(r.data.items)).catch(() => {});
    if (tab === "businesses") api.get("/admin/businesses").then((r) => setBusinesses(r.data.items)).catch(() => {});
    if (tab === "leads") api.get("/admin/leads").then((r) => setLeads(r.data.items)).catch(() => {});
    if (tab === "health") api.get("/admin/api-health").then((r) => setHealth(r.data.items)).catch(() => {});
    if (tab === "audit") api.get("/admin/audit-logs").then((r) => setAudit(r.data.items)).catch(() => {});
  }, [tab]);

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/reports/${id}/moderate`, { action });
      setReports((rs) => rs.map((r) => (r.id === id ? { ...r, moderation_status: action === "approve" ? "approved" : action === "hide" ? "hidden" : action === "remove" ? "removed" : r.moderation_status } : r)));
      toast.success("Report updated");
    } catch { toast.error("Please try again"); }
  };
  const verifyBiz = async (id) => {
    try {
      await api.post(`/admin/businesses/${id}/verify`);
      setBusinesses((bs) => bs.map((b) => (b.id === id ? { ...b, verified: true } : b)));
      toast.success("Verified");
    } catch { toast.error("Please try again"); }
  };

  const tabs = [
    ["dashboard", "Overview"],
    ["areas", "Surf areas"],
    ["reports", "Reports"],
    ["businesses", "Businesses"],
    ["leads", "Leads"],
    ["health", "API health"],
    ["audit", "Audit log"],
  ];

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="admin-page">
      <div className="overline">Admin portal</div>
      <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">Surfmatics admin</h1>

      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`admin-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm border ${
              tab === k ? "bg-teal-800 text-white border-teal-800" : "bg-white border-border hover:border-teal-800/50"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "dashboard" && d && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-dashboard">
            {[
              ["Users", d.users],
              ["Surf areas", d.surf_areas],
              ["Published", d.published_areas],
              ["Reports", d.reports_total],
              ["Flagged", d.reports_pending],
              ["Businesses", d.businesses],
              ["Leads", d.leads],
              ["Recommendations", d.recommendation_records],
            ].map(([l, v]) => (
              <div key={l} className="card-soft p-5">
                <div className="overline">{l}</div>
                <div className="font-display text-3xl text-teal-900 mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "areas" && (
          <div>
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowCreate(true)}
                data-testid="admin-new-area"
                className="h-9 px-4 rounded-full text-sm bg-terracotta-500 text-white hover:bg-terracotta-600"
              >
                + New surf area
              </button>
            </div>
            <div className="card-soft overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-areas-table">
                <thead className="bg-sand-100 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="p-3">Name</th><th className="p-3">Region</th><th className="p-3">Ability</th><th className="p-3">Visibility</th><th className="p-3">Coords</th><th className="p-3">Published</th><th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {areas.map((a) => (
                    <tr key={a.id} className="border-t border-border">
                      <td className="p-3 text-teal-900 font-medium">
                        <Link to={`/surf-area/${a.slug}`} className="hover:underline">{a.name}</Link>
                      </td>
                      <td className="p-3">{a.region}, {a.country}</td>
                      <td className="p-3">{a.min_ability} → {a.max_ability}</td>
                      <td className="p-3">{a.visibility}</td>
                      <td className="p-3 text-xs text-muted-foreground">{a.lat?.toFixed(3)}, {a.lon?.toFixed(3)}</td>
                      <td className="p-3">{a.published ? "Yes" : "No"}</td>
                      <td className="p-3">
                        <button
                          onClick={() => setEditArea(a)}
                          data-testid={`edit-area-${a.slug}`}
                          className="text-xs px-3 py-1 rounded-full border border-border hover:border-teal-800/50 bg-white"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "reports" && (
          <div className="space-y-3" data-testid="admin-reports">
            {reports.length === 0 && <div className="text-sm text-muted-foreground">No reports yet.</div>}
            {reports.map((r) => (
              <div key={r.id} className="card-soft p-4 flex flex-wrap items-center gap-3 justify-between">
                <div className="text-sm">
                  <div className="text-teal-900 font-medium">{r.user_name || "Anonymous"}</div>
                  <div className="text-xs text-muted-foreground">
                    quality {r.surf_quality} · crowd {r.crowd_level} · beg {r.beginner_suitable} · status: {r.moderation_status}
                  </div>
                  {r.comment && <div className="text-xs mt-1">"{r.comment}"</div>}
                </div>
                <div className="flex gap-2">
                  {["approve", "hide", "remove"].map((a) => (
                    <button
                      key={a}
                      onClick={() => moderate(r.id, a)}
                      data-testid={`mod-${a}-${r.id}`}
                      className="text-xs px-3 py-1 rounded-full border border-border hover:border-teal-800/50 bg-white"
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "businesses" && (
          <div className="space-y-3" data-testid="admin-businesses">
            {businesses.map((b) => (
              <div key={b.id} className="card-soft p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-teal-900">{b.name}</div>
                  <div className="text-xs text-muted-foreground">{b.town}, {b.region} · {b.business_type.replace(/_/g, " ")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${b.verified ? "bg-teal-800 text-white" : "bg-sand-200"}`}>
                    {b.verified ? "Verified" : "Unverified"}
                  </span>
                  {!b.verified && (
                    <button onClick={() => verifyBiz(b.id)} data-testid={`verify-${b.id}`} className="text-xs px-3 py-1 rounded-full bg-teal-800 text-white">
                      Verify
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads">
            {leads.length === 0 && <div className="text-sm text-muted-foreground">No enquiries yet.</div>}
            {leads.map((l) => (
              <div key={l.id} className="card-soft p-4">
                <div className="text-sm">
                  <b>{l.name}</b> ({l.email}) · party {l.party_size}
                </div>
                <div className="text-xs text-muted-foreground mt-1">business: {l.business_id}</div>
                {l.message && <div className="mt-2 text-sm">"{l.message}"</div>}
              </div>
            ))}
          </div>
        )}

        {tab === "health" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-health">
            {health.map((h) => (
              <div key={h.id} className="card-soft p-5">
                <div className="overline">{h.provider}</div>
                <div className="mt-2 text-sm">Status: <b className={h.status === "ok" ? "text-teal-600" : "text-terracotta-700"}>{h.status}</b></div>
                <div className="text-xs text-muted-foreground mt-1">Last checked: {h.last_checked_at}</div>
                {h.last_error && <div className="text-xs text-terracotta-700 mt-1">{h.last_error}</div>}
              </div>
            ))}
            {health.length === 0 && <div className="text-sm text-muted-foreground">No provider health records yet — trigger a recommendation to warm the cache.</div>}
          </div>
        )}

        {tab === "audit" && (
          <div className="card-soft overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-audit-table">
              <thead className="bg-sand-100 text-left uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="p-3">When</th><th className="p-3">Who</th><th className="p-3">Action</th><th className="p-3">Entity</th><th className="p-3">Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={a.id} className="border-t border-border">
                    <td className="p-3">{a.timestamp}</td>
                    <td className="p-3">{a.actor_email}</td>
                    <td className="p-3 font-medium text-teal-900">{a.action}</td>
                    <td className="p-3">{a.entity} · {a.entity_id?.slice(0, 8)}…</td>
                    <td className="p-3 max-w-[280px] truncate">{JSON.stringify(a.details)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(editArea || showCreate) && (
        <SurfAreaEditModal
          area={editArea}
          onClose={() => { setEditArea(null); setShowCreate(false); }}
          onSaved={() => { setEditArea(null); setShowCreate(false); reloadAreas(); }}
        />
      )}
    </div>
  );
}
